<template>
<v-container class="py-0">
  <!-- Total Premium and Filter -->
  <v-row wrap>
    <v-col class="black--text">
     {{ totalDisplayText }}
    </v-col>
    <v-col v-if="filterOptions.length > 2" cols="12" class="py-0">
      <v-select v-model="selectedOption" :items="filterOptions" label="Filter Coverages" hide-details @focus="sendFilterEvent">
        <template slot="item" slot-scope="data">
            <v-icon v-if="data.item.text.startsWith('Location')" class="blue3--text">mdi-map</v-icon>
            {{ data.item.text }}
        </template>

        <template slot="selection" slot-scope="data">
            <v-icon v-if="data.item.text.startsWith('Location')" class="blue3--text">mdi-map</v-icon>
            {{ data.item.text }}
        </template>
      </v-select>
    </v-col>
  </v-row>

  <!-- Top Level Coverages ( Policy or Line of Business depending on view) -->
  <template v-if="selectedOption <= 0">
    <template v-for="(coverage, c1) in formattedCoverages.coverages">
      <v-row :key="`cov-${c1}`">
        <v-col cols="12" class="py-1">
          <CoverageCard :coverage="coverage" />
        </v-col>
      </v-row>
    </template>
  </template>

  <template v-for="(location, l) in formattedCoverages.locationCoverages">
    <template v-if="selectedOption === -1 || selectedOption === `locCov-${location.number}`">
    <!-- Location Name Row -->
      <v-row v-if="selectedOption === -1" :key="`loc-${l}`" class="locationRow">
        <v-col class="subtitle-1 py-0">
          <v-icon class="blue3--text">mdi-map</v-icon>
          <span>Location {{ location.number }}</span>
          <span v-if="(location.name || null) !== null && location.name.trim() !== ''"> - {{ location.name }}</span>
        </v-col>
      </v-row>

      <template v-for="(locCov, lc1) in location.coverages">
        <v-row :key="`locCov-${lc1}`" wrap>
          <v-col cols="12" class="py-1">
            <CoverageCard :coverage="locCov" />
          </v-col>
        </v-row>
      </template>

      <template v-for="(building, b) in location.buildingCoverages">
        <v-row :key="`loc-${l}-build-${b}`">
          <v-col class="subtitle-1 py-1">
            <v-icon class="blue3--text">mdi-office-building</v-icon>
            <span>Building {{ building.number }}</span>
            <span v-if="(building.name || null) !== null && building.name.trim() !== ''"> - {{ building.name }}</span>
          </v-col>
        </v-row>

        <template v-for="(bCov, bc1) in building.coverages">
          <v-row :key="`loc-${l}-build-${b}-bCov-${bc1}`">
            <v-col cols="12" class="py-1">
              <CoverageCard :coverage="bCov" />
            </v-col>
          </v-row>
        </template>
      </template>
    </template>
  </template>

  <template v-for="(lineOfBus, lobIndex) in formattedCoverages.lineOfBusinessCoverages">
    <template>
      <v-row v-if="shouldDisplayLineOfBusinessText(lineOfBus)" :key="`lob-${lineOfBus.code}`" class="lobRow">
        <v-col class="subtitle-1 py-0">
          <span>Line of Business - {{ lobCodes[lineOfBus.code] }}</span>
        </v-col>
      </v-row>

      <!-- Line of Business Coverages -->
      <template v-if="selectedOption === -1 || selectedOption === `lob-${lineOfBus.code}`">
        <template v-for="(coverage, c1) in lineOfBus.coverages">
          <v-row :key="`lob-${lineOfBus.code}-cov-${c1}`">
            <v-col cols="12" class="py-1">
              <CoverageCard :coverage="coverage" :color="lobColors[lobIndex % 2]" />
            </v-col>
          </v-row>
        </template>
      </template>

      <!-- Line of Business Locations -->
      <template v-for="(location, l) in lineOfBus.locationCoverages">
        <template v-if="selectedOption === -1 || selectedOption === `lob-${lineOfBus.code}` || selectedOption === `loc-${location.number}`">
          <v-row v-if="selectedOption !== `loc-${location.number}`" :key="`lob-${lineOfBus.code}-loc-${l}`" class="locationRow">
            <v-col class="subtitle-1 py-0">
              <v-icon class="blue3--text">mdi-map</v-icon>
              <span>Location {{ location.number }}</span>
              <span v-if="(location.name || null) !== null && location.name.trim() !== ''"> - {{ location.name }}</span>
            </v-col>
          </v-row>

          <template v-for="(locCov, lc1) in location.coverages">
            <v-row :key="`lob-${lineOfBus.code}-locCov-${lc1}`" wrap>
              <v-col cols="12" class="py-1">
                <CoverageCard :coverage="locCov" :color="lobColors[lobIndex % 2]" />
              </v-col>
            </v-row>
          </template>

          <template v-for="(building, b) in location.buildingCoverages">
            <v-row :key="`lob-${lineOfBus.code}-loc-${l}-build-${b}`">
              <v-col class="subtitle-1 py-1">
                <v-icon class="blue3--text">mdi-office-building</v-icon>
                <span>Building {{ building.number }}</span>
                <span v-if="(building.name || null) !== null && building.name.trim() !== ''"> - {{ building.name }}</span>
              </v-col>
            </v-row>

            <template v-for="(bCov, bc1) in building.coverages">
              <v-row :key="`lob-${lineOfBus.code}-loc-${l}-build-${b}-bCov-${bc1}`">
                <v-col cols="12" class="py-1">
                  <CoverageCard :coverage="bCov" :color="lobColors[lobIndex % 2]" />
                </v-col>
              </v-row>
            </template>
          </template>
        </template>
      </template>
    </template>
  </template>

</v-container>
</template>

<style scoped>
</style>

<script>
import CoverageCard from '@/components/Shared/CoverageCard.vue';
import { mapState, mapActions } from 'vuex';
import displayFormatterService from '@/services/shared/displayFormatterService.js';
import lobCodeService from '@/services/clientViewer/lobCodeService.js';
import analyticsService from '@/services/shared/analyticsService.js';

export default {
  name: 'Policies',
  components: { CoverageCard },
  props: {
    coverages: {
      type: Object,
      default: null
    },
    lob: {
      type: Boolean,
      required: false
    },
    policy: {
      type: Boolean,
      required: false
    }
  },
  data: function () {
    var options = [
      { text: 'All', value: -1 }
    ];

    if (this.policy) {
      options.push({ text: 'Policy', value: 0 });
    } else if (this.lob) {
      options.push({ text: 'Line of Business', value: 0 });
    } else {
      // If we are here, we goofed.
      options.push({ text: 'Top Level', value: 0 });
    }

    return {
      loading: true,
      totalPremium: 0,
      formattedCoverages: [],
      // the currently selected filter
      selectedOption: options[0].value,
      baseOptions: options,
      // all options available to filter on
      filterOptions: options,
      // all line of business options to filter on
      lineOfBusinessFilterOptions: [],
      // all location options to filter on
      locationFilterOptions: [],

      lobColors: [
        'blue6',
        'blue5'
      ]
    };
  },
  computed: {
    ...mapState(['selectedLineOfBusinessCode']),
    clientId: function () {
      return this.$route.params.clientId;
    },
    policyOrderNumber: function () {
      return this.$route.params.policyOrderNumber;
    },
    noDataText: function () {
      return this.error ? 'An error occurred fetching coverages' : 'This line of business has no coverages';
    },
    totalDisplayText: function () {
      return `Total Premium: ${displayFormatterService.formatCurrency(this.totalPremium)}`;
    },
    lobCodes: function () {
      return lobCodeService.lobCodes;
    }
  },
  watch: {
    coverages: function (data) {
      this.resetFilterOptions();
      // Process top level coverages model (base class in C#)
      var formatted = this.processCoveragesModel(data);

      // If its a policy, try and process lob coverages
      if (this.policy) {
        if ((data.lineOfBusinessCoverages || null) !== null) {
          for (var i = 0; i < data.lineOfBusinessCoverages.length; i++) {
            var lob = data.lineOfBusinessCoverages[i];
            this.addLineOfBusinessFilterOption({ text: this.lobCodes[lob.code], value: `lob-${lob.code}` });

            var lobCov = this.processCoveragesModel(data.lineOfBusinessCoverages[i]);
            lobCov.code = data.lineOfBusinessCoverages[i].code;
            formatted.lineOfBusinessCoverages.push(lobCov);
          }
        }
      }

      // Set new filter values
      this.filterOptions = this.baseOptions
        .concat(this.locationFilterOptions)
        .concat(this.lineOfBusinessFilterOptions);

      this.formattedCoverages = formatted;
    }
  },
  methods: {
    ...mapActions(['setError']),
    toggleView: function () {
      this.viewingMore = !this.viewingMore;
    },
    shouldDisplayLineOfBusinessText: function (lineOfBusiness) {
      // Only display text above a line of business if selected filter is 'All' or a Location covered on that Line of Business
      return this.selectedOption === -1 || this.hasSelectedLocation(lineOfBusiness);
    },
    hasSelectedLocation: function (coverageViewModel) {
      return coverageViewModel.locationCoverages.findIndex(x => `loc-${x.number}` === this.selectedOption) >= 0;
    },
    formatCoverages: function (coverages) {
      coverages.forEach(x => {
        this.totalPremium += isNaN(x.premium) ? 0 : x.premium;
      });

      coverages.sort((a, b) => a.displayOrder - b.displayOrder);

      return coverages;
    },
    addLocationFilterOption: function (option) {
      var existingIndex = this.locationFilterOptions.findIndex(x => x.text === option.text && x.value === option.value);
      if (existingIndex >= 0) return;

      this.locationFilterOptions.push(option);
    },
    addLineOfBusinessFilterOption: function (option) {
      var existingIndex = this.lineOfBusinessFilterOptions.findIndex(x => x.text === option.text && x.value === option.value);
      if (existingIndex >= 0) return;

      this.lineOfBusinessFilterOptions.push(option);
    },
    processCoveragesModel: function (coveragesModel) {
      var formatted = {
        coverages: [],
        locationCoverages: [],
        lineOfBusinessCoverages: []
      };

      formatted.coverages = this.formatCoverages(coveragesModel.coverages);

      if ((coveragesModel.locationCoverages || null) !== null) {
        coveragesModel.locationCoverages.forEach(lc => {
          var locName = `Location ${lc.number}`;
          if ((lc.name || null) !== null && lc.name.trim() !== '') {
            locName += ` - ${lc.name}`;
          }
          this.addLocationFilterOption({ text: locName, value: `loc-${lc.number}` });

          var locCov = {
            name: lc.name,
            number: lc.number
          };

          locCov.coverages = this.formatCoverages(lc.coverages);
          locCov.buildingCoverages = [];

          lc.buildingCoverages.forEach(bc => {
            var bCov = {
              name: bc.name,
              number: bc.number,
              coverages: this.formatCoverages(bc.coverages).sort((bc1, bc2) => bc1.displayOrder - bc2.displayOrder)
            };
            locCov.buildingCoverages.push(bCov);
          });

          if (locCov.buildingCoverages.length > 1) {
            locCov.buildingCoverages.sort((b1, b2) => b1.number - b2.number);
          }

          formatted.locationCoverages.push(locCov);
        });

        if (formatted.locationCoverages.length > 1) {
          formatted.locationCoverages.sort((l1, l2) => l1.number - l2.number);
        }
      }

      return formatted;
    },
    resetFilterOptions: function () {
      this.filterOptions = this.baseOptions;
      this.locationFilterOptions = [];
      this.lineOfBusinessFilterOptions = [];
    },
    sendFilterEvent: function () {
      analyticsService.sendEvent(this.$gtag, 'Filter', 'Policy', 'Policy Coverages Filter');
    }
  }
};
</script>
